import type { IEvaluationProject } from '../../../Model/Project/IEvaluationProject.ts'
import type { JSX } from 'react'
import { Fragment, useEffect, useState } from 'react'
import { PencilIcon, UserPlusIcon } from '@heroicons/react/24/outline'
import { useModal } from '../../Modal/UseModal.js'
import { DocumentTextIcon } from '@heroicons/react/24/outline'
import { TooltipTrigger } from '../../../Provider/TooltipManager.js'
import { useDispatch } from 'react-redux'
import Actions from '../../../Flux/index.js'
import { projectSlice } from '../../../Flux/Slice/Project/ProjectSlice.js'

interface ProjectContextActionsProps {
  project: IEvaluationProject
  maxItemsToShow: number
  readOnly: boolean
}

export const ProjectContextActions = ({ project, maxItemsToShow, readOnly }: ProjectContextActionsProps) => {
  const [visibleActions, setVisibleActions] = useState<((inDropdown: boolean) => JSX.Element)[]>([])
  const [hiddenActions, setHiddenActions] = useState<((inDropdown: boolean) => JSX.Element)[]>([])
  const conditionalAction = (
    condition: unknown,
    element: (inDropdown: boolean) => JSX.Element
  ): ((inDropdown: boolean) => JSX.Element) | null => {
    if (condition) {
      return element
    }
    return null
  }

  const { openModal } = useModal()

  const dropdown = (inDropdown: boolean, classes: string): string => `${classes}${inDropdown ? ' w-full' : ''}`

  const dispatch = useDispatch()

  useEffect(() => {
    console.log('in project context actions', readOnly)
    const allActions = [
      conditionalAction(!readOnly, (inDropdown) => (
        <button
          className={dropdown(inDropdown, `btn-sm btn btn-primary`)}
          data-testid="edit-project"
          onClick={() => openModal('editProject', { project })}
        >
          <PencilIcon className="w-4 h-4" />
          Edit Project
        </button>
      )),
      conditionalAction(!readOnly, (inDropdown) => (
        <button
          className={dropdown(inDropdown, `btn-sm btn btn-primary`)}
          data-testid="invite-user"
          onClick={() => openModal('inviteParticipant', { project })}
        >
          <UserPlusIcon className="w-4 h-4" />
          Invite User
        </button>
      )),
      conditionalAction(!readOnly, (inDropdown) => (
        <button
          className={dropdown(inDropdown, `btn-sm btn btn-primary`)}
          data-testid="draft-project"
          onClick={() =>
            dispatch(
              Actions[projectSlice.name].toggleDraftModeRequest({
                projectId: project.id,
                version: project.version ?? 'unknown',
                draftMode: !project.draftMode
              })
            )
          }
        >
          <TooltipTrigger
            classNames={'inline-flex'}
            tooltip={
              'In Draft Mode, evaluation data will not be auto-refreshed when samples are added or removed. This can be used to batch-add samples from several orders without wait-time.'
            }
          >
            <DocumentTextIcon className="w-4" />
            {project.draftMode ? 'Exit Draft Mode' : 'Activate Draft Mode'}
          </TooltipTrigger>
        </button>
      )),
      conditionalAction(!readOnly, (inDropdown) => (
        <button
          className={dropdown(inDropdown, `btn-sm btn btn-primary`)}
          data-testid="report-project"
          onClick={() => openModal('projectReport', { project })}
        >
          <TooltipTrigger
            classNames={'inline-flex'}
            tooltip={'Merge all contained samples into a single XLSX report. Report is generated on project changes.'}
          >
            Download Project Report
          </TooltipTrigger>
        </button>
      ))
    ].filter((x) => !!x)

    setVisibleActions(allActions.slice(0, maxItemsToShow))
    setHiddenActions(allActions.slice(maxItemsToShow))
  }, [project, readOnly])

  return (
    <>
      {visibleActions.map((action, index) => (
        <Fragment key={`vis-${index}`}>
          <div className="ml-2">{action(false)}</div>
        </Fragment>
      ))}
      {hiddenActions.length > 0 && (
        <div className="dropdown dropdown-end">
          <button data-testid="burger-menu" tabIndex={0} className="btn btn-sm btn-link">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="size-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM12.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM18.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z"
              />
            </svg>
          </button>
          <ul tabIndex={0} className="z-50 dropdown-content menu p-2 shadow-sm bg-base-100 rounded-box w-52">
            {hiddenActions.map((action, index) => (
              <li key={`hid-${index}`} className="m-1">
                {action(true)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  )
}
